import { LogOut, User, ChevronDown } from "lucide-react"
import { Button } from "@/components/ui/button"
import { useToast } from "@/hooks/use-toast"
import { useNavigate } from "react-router-dom"
import { useEffect, useCallback } from "react"
import { supabase } from "@/integrations/supabase/client"
import { useTheme } from "@/contexts/ThemeContext"
import { useAuth } from "@/contexts/AuthContext"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"

interface SignOutButtonProps {
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
  size?: "default" | "sm" | "lg" | "icon"
  showIcon?: boolean
}

const SignOutButton = ({ variant = "ghost", size = "default", showIcon = true }: SignOutButtonProps) => {
  const { toast } = useToast()
  const navigate = useNavigate()
  const { theme } = useTheme()
  const { userName, hasFullAccess } = useAuth()
  const isLightMode = theme === 'light'

  const handleSignOut = useCallback(async (e: React.MouseEvent) => {
    // Prevent any event bubbling
    e.preventDefault()
    e.stopPropagation()
    
    console.log("Sign out button clicked") // Debug log
    
    try {
      // Clear localStorage first
      localStorage.removeItem('dashboard_authorized')
      localStorage.removeItem('dashboard_encryption_key')
      localStorage.removeItem('dashboard_user_access')
      localStorage.removeItem('dashboard_user_id')
      localStorage.removeItem('dashboard_user_name')
      
      // Clear any Supabase auth keys
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('supabase.auth.') || key.includes('sb-')) {
          localStorage.removeItem(key)
        }
      })
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut({ scope: 'global' })
      
      if (error) {
        console.error("Supabase sign out error:", error)
        // Don't throw error, continue with logout process
      }
      
      // Show success toast
      toast({
        title: "Signed out successfully",
        description: "You have been signed out of the KOL Dashboard",
      })
      
      // Force redirect with page refresh
      setTimeout(() => {
        window.location.href = '/auth'
      }, 100)
      
    } catch (error) {
      console.error("Sign out error:", error)
      toast({
        title: "Error signing out",
        description: "An error occurred while signing out",
        variant: "destructive"
      })
      
      // Force logout anyway
      setTimeout(() => {
        window.location.href = '/auth'
      }, 1000)
    }
  }, [toast])

  // Monitor for access or encryption key changes
  const checkUserStatus = useCallback(async () => {
    const userId = localStorage.getItem('dashboard_user_id');
    
    if (!userId) return;
    
    try {
      // Get current session
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.log("No active session, logging out...");
        handleSignOut({ preventDefault: () => {}, stopPropagation: () => {} } as React.MouseEvent);
        return;
      }
      
      // Check if the user's access level is still valid
      const { data: userData, error } = await supabase
        .from('kol_pic')
        .select('access')
        .eq('id', userId)
        .single();
      
      if (error) {
        console.error("Error checking user status:", error);
        return;
      }
      
      // If access has changed from what's in localStorage, log the user out
      const currentAccess = localStorage.getItem('dashboard_user_access');
      if (!userData || userData.access !== currentAccess) {
        console.log("User access level changed, logging out...");
        
        toast({
          title: "Session Expired",
          description: "Your access level has been changed",
          variant: "destructive"
        });
        
        // Give the toast time to show before redirecting
        setTimeout(() => {
          handleSignOut({ preventDefault: () => {}, stopPropagation: () => {} } as React.MouseEvent);
        }, 1500);
      }
    } catch (err) {
      console.error("Failed to verify user status:", err);
    }
  }, [toast, handleSignOut]);

  useEffect(() => {
    // Check user status on mount
    checkUserStatus();
    
    // Set up interval to periodically check user status (every 60 seconds)
    const intervalId = setInterval(checkUserStatus, 60000);
    
    return () => clearInterval(intervalId);
  }, [checkUserStatus]);

  // Get user initials for avatar
  const getUserInitials = (name: string | null) => {
    if (!name) return "U"
    return name
      .split(" ")
      .map(part => part.charAt(0).toUpperCase())
      .slice(0, 2)
      .join("")
  }

  // Format access level for display
  const getAccessDisplayText = (hasFullAccess: boolean) => {
    return hasFullAccess ? "Full Access" : "Limited Access"
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant={variant} 
          size={size}
          className={`
            ${isLightMode ? 'text-gray-800 hover:text-black' : 'text-dashboard-text-primary hover:text-dashboard-accent'}
            cursor-pointer
            pointer-events-auto
            z-10
            relative
            flex items-center gap-2
          `}
          style={{ pointerEvents: 'auto' }}
        >
          <Avatar className="h-6 w-6">
            <AvatarFallback className="text-xs bg-primary/10 text-primary">
              {getUserInitials(userName)}
            </AvatarFallback>
          </Avatar>
          {size !== "icon" && (
            <>
              <span className="hidden sm:inline">{userName || "User"}</span>
              <ChevronDown className="h-3 w-3" />
            </>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56 bg-background border-border">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">
              {userName || "User"}
            </p>
            <p className="text-xs leading-none text-muted-foreground">
              {getAccessDisplayText(hasFullAccess)}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

export default SignOutButton
